import React, { useState, useCallback, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Upload, Settings, Plus, Cloud, X, LayoutGrid, List } from 'lucide-react'
import CreativeMatrix from './CreativeMatrix'
import GoogleDriveBrowser from '../GoogleDriveBrowser'
import ExportMatrix from './ExportMatrix'
import { apiService } from '@/services/api'
import AssetCard from './AssetCard'
import AssetTableRow from './AssetTableRow'
import AssetStatsBar from './AssetStatsBar'
import AssetDetailPanel from './AssetDetailPanel'
import { processBRDCompliance } from '@/utils/complianceChecker'

interface Asset {
  id: string
  name: string
  type: string
  size: number
  url?: string
  uploadDate: string
  lastModified: string
  status?: 'uploading' | 'analyzing' | 'complete' | 'error'
  progress?: number
  thumbnailLink?: string
  webViewLink?: string
  duration?: number
  dimensions?: string
  channel?: string
  format?: string
  saveStatus?: 'saving' | 'saved' | 'failed' | 'unsaved'
  lastSavedAt?: string
  processingSteps?: Array<{
    label: string
    status: 'pending' | 'active' | 'done' | 'error'
  }>
  analysis?: {
    campaignName?: string
    fdaFilingNumber?: string
    customerType?: string
    complianceScore?: number
    summary?: {
      passed: number
      warnings: number
      failed: number
    }
    issues?: Array<{
      id?: number
      rule: string
      severity: 'critical' | 'high' | 'medium' | 'warning' | 'minor' | 'info'
      message: string
      status: 'pass' | 'fail' | 'warning' | 'not_checked'
      channel?: string
      field?: string
      section?: string
      detectedValue?: string
      expectedValue?: string
      requiredAction?: string
      gridLocation?: string
      coordinates?: { x: number; y: number; width: number; height: number }
      source?: 'Built-in' | 'Custom BRD' | 'AI Analysis'
      reasoning?: string
      reviewed?: boolean
    }>
    recommendations?: string[]
    frames?: Array<{
      timestamp: number
      thumbnailUrl: string
      label?: string
      issues: Array<{ severity: string; message: string }>
    }>
  }
}

interface BRDRule {
  id: number
  rule: string
  severity: 'critical' | 'warning' | 'minor'
  field: string
  section: string
  description: string
}

interface BRDCampaignBuilderProps {
  initialAssets?: any[]
  brdRules?: any[]
  loadingAssets?: boolean
  onAssetAdd?: (asset: any) => void
  campaignName?: string
  campaignDescription?: string
}

export default function BRDCampaignBuilder(props: BRDCampaignBuilderProps = {}) {
  const [activeTab, setActiveTab] = useState<'upload' | 'matrix'>('upload')
  const [assets, setAssets] = useState<Asset[]>([])
  const [brdRules, setBrdRules] = useState<BRDRule[]>([])
  const [showGoogleDrive, setShowGoogleDrive] = useState(false)
  const loadingAssets = props.loadingAssets || false
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name'>('newest')
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  console.log('[BRDCampaignBuilder v2] RENDERING with new component architecture')

  // Load existing assets from props
  React.useEffect(() => {
    if (props.initialAssets && props.initialAssets.length > 0) {
      console.log('✅ BRDCampaignBuilder received initialAssets:', props.initialAssets.length, {
        firstAssetKeys: Object.keys(props.initialAssets[0] || {}),
        hasUrl: !!props.initialAssets[0]?.url,
        hasThumbnail: !!props.initialAssets[0]?.thumbnailLink
      })
      setAssets(props.initialAssets.map(asset => ({
        ...asset,
        status: 'complete' as const,
        progress: 100, // ← Set to 100% for completed assets
        processingSteps: [
          { label: 'Upload/Extract', status: 'done' as const },
          { label: 'OCR & Text', status: 'done' as const },
          { label: 'Elements Detection', status: 'done' as const },
          { label: 'BRD Matching', status: 'done' as const },
          { label: 'Compliance Score', status: 'done' as const }
        ]
      })))
    }
  }, [props.initialAssets])

  // Load user's BRD rules on component mount
  React.useEffect(() => {
    loadUserBRDRules()
    if (props.brdRules && props.brdRules.length > 0) {
      console.log('[BRDCampaignBuilder] Using provided BRD rules:', props.brdRules.length)
      setBrdRules(props.brdRules)
    }
  }, [props.brdRules])

  const loadUserBRDRules = async () => {
    try {
      const data: any = await apiService.request('/api/user-data/brd-rules')
      if (data?.success) {
        const rules = data?.data?.rules?.filter((rule: any) => rule.enabled) || []
        setBrdRules(rules)
        console.log('Loaded user BRD rules:', rules.length)
      }
    } catch (error) {
      console.error('Failed to load user BRD rules:', error)
    }
  }

  // Handle asset file uploads
  const handleAssetUpload = useCallback(async (files: File[]) => {
    const newAssets: Asset[] = files.map(file => ({
      id: `asset-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      type: file.type.startsWith('image/') ? 'image' : 
            file.type.startsWith('video/') ? 'video' : 'document',
      size: file.size,
      uploadDate: new Date().toISOString().split('T')[0],
      lastModified: new Date().toISOString().split('T')[0],
      status: 'uploading',
      progress: 5,
      processingSteps: [
        { label: 'Upload/Extract', status: 'active' },
        { label: 'OCR & Text', status: 'pending' },
        { label: 'Elements Detection', status: 'pending' },
        { label: 'BRD Matching', status: 'pending' },
        { label: 'Compliance Score', status: 'pending' }
      ]
    }))

    setAssets(prev => [...prev, ...newAssets])

    // Process assets concurrently (up to 3 at a time)
    const CONCURRENT_LIMIT = 3
    for (let i = 0; i < newAssets.length; i += CONCURRENT_LIMIT) {
      const batch = newAssets.slice(i, i + CONCURRENT_LIMIT)
      const batchFiles = Array.from(files).slice(i, i + CONCURRENT_LIMIT)
      
      // Process this batch concurrently
      await Promise.all(
        batch.map((asset, batchIndex) => 
          processAssetWithSteps(asset, batchFiles[batchIndex])
        )
      )
    }
  }, [])

  const processAssetWithSteps = async (asset: Asset, file: File) => {
    const stepDelays = [800, 1200, 1000, 1500, 800] // Realistic processing times
    
    try {
      // Process each step
      for (let i = 0; i < 5; i++) {
        await new Promise(resolve => setTimeout(resolve, stepDelays[i]))
        
        // Update step status
        setAssets(prev => prev.map(a => {
          if (a.id !== asset.id) return a
          
          const updatedSteps = [...(a.processingSteps || [])]
          if (i > 0 && updatedSteps[i - 1]) updatedSteps[i - 1].status = 'done'
          if (updatedSteps[i]) updatedSteps[i].status = 'active'
          
          return {
            ...a,
            progress: Math.min(100, 5 + (i + 1) * 19),
            processingSteps: updatedSteps,
            status: i === 4 ? 'analyzing' : a.status
          }
        }))
        
        // Call real AI analysis on the final step
        if (i === 4) {
          const analysisResult = await callRealAIAnalysis(file)
          const complianceResult = processBRDCompliance(analysisResult, asset, brdRules)
          
          // Store results in database
          await storeProcessingResult(asset, complianceResult, analysisResult)
          
          // Create updated asset with analysis
          const finalSteps = [...(asset.processingSteps || [])]
          finalSteps[4].status = 'done'
          
          const completedAsset: Asset = {
            ...asset,
            progress: 100,
            status: 'complete' as const,
            processingSteps: finalSteps,
            analysis: complianceResult
          }
          
          // Update local state - mark as saving
          setAssets(prev => prev.map(a => 
            a.id !== asset.id ? a : { ...completedAsset, saveStatus: 'saving' as const }
          ))
          
          // Save to campaign via parent callback
          if (props.onAssetAdd) {
            try {
              await props.onAssetAdd(completedAsset)
              console.log('✅ Asset saved to campaign:', asset.name)
              
              // Mark as successfully saved
              setAssets(prev => prev.map(a => 
                a.id !== asset.id ? a : { 
                  ...completedAsset, 
                  saveStatus: 'saved' as const,
                  lastSavedAt: new Date().toISOString()
                }
              ))
            } catch (saveError) {
              console.error('❌ Failed to save asset to campaign:', saveError)
              setAssets(prev => prev.map(a => 
                a.id !== asset.id ? a : { 
                  ...completedAsset, 
                  saveStatus: 'failed' as const
                }
              ))
            }
          } else {
            // No onAssetAdd callback - mark as unsaved
            console.warn('⚠️  No onAssetAdd callback - asset not persisted')
            setAssets(prev => prev.map(a => 
              a.id !== asset.id ? a : { 
                ...completedAsset, 
                saveStatus: 'unsaved' as const
              }
            ))
          }
        }
      }
    } catch (error) {
      console.error('Processing failed for asset:', asset.name, error)
      setAssets(prev => prev.map(a => {
        if (a.id !== asset.id) return a
        
        const errorSteps = [...(a.processingSteps || [])]
        const activeIndex = errorSteps.findIndex(s => s.status === 'active')
        if (activeIndex >= 0) errorSteps[activeIndex].status = 'error'
        
        return {
          ...a,
          status: 'error',
          processingSteps: errorSteps
        }
      }))
    }
  }

  const callRealAIAnalysis = async (file: File) => {
    try {
      const data: any = await apiService.request('/api/ai-analysis/analyze-asset', {
        method: 'POST',
        body: JSON.stringify({
          asset: {
            name: file.name,
            mimeType: file.type,
            size: file.size
          }
        })
      })

      return data?.data?.analysis || {}
    } catch (error) {
      console.error('AI analysis error:', error)
      return {}
    }
  }

  // Note: processBRDCompliance is now imported from @/utils/complianceChecker

  const storeProcessingResult = async (asset: Asset, result: any, aiResult: any) => {
    try {
      await apiService.request('/api/user-data/processing-results', {
        method: 'POST',
        body: JSON.stringify({
          timestamp: Date.now(),
          assetId: asset.id,
          assetName: asset.name,
          complianceScore: result.complianceScore,
          issues: result.issues,
          recommendations: result.recommendations,
          aiAnalysis: aiResult
        })
      })
    } catch (error) {
      console.error('Failed to store processing result:', error)
    }
  }

  // Handle drag and drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleAssetUpload(files)
    }
  }, [handleAssetUpload])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  // Handle file input change
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleAssetUpload(Array.from(e.target.files))
    }
  }, [handleAssetUpload])

  // Handle Google Drive import
  const handleGoogleDriveImport = useCallback(async (files: any[]) => {
    const driveAssets: Asset[] = files.map(file => {
      // Extract file ID from webViewLink or use file.id directly
      const fileId = file.id
      
      // Construct proper preview URL based on file type
      let previewUrl = file.webViewLink
      // Use backend proxy for authenticated file access (handles token refresh automatically)
      if (fileId) {
        previewUrl = `${apiService.getBaseUrl()}/api/google-drive/proxy/${fileId}`
      } else if (file.thumbnailLink) {
        // Fallback to thumbnail if no file ID
        previewUrl = file.thumbnailLink
      }
      
      console.log(`📥 [Import] Processing ${file.name}:`, {
        fileId,
        mimeType: file.mimeType,
        originalWebViewLink: file.webViewLink,
        thumbnailLink: file.thumbnailLink,
        generatedPreviewUrl: previewUrl
      })
      
      return {
        id: `gd-${fileId}`,
        name: file.name,
        type: file.mimeType?.startsWith('image/') ? 'image' : 
              file.mimeType?.startsWith('video/') ? 'video' : 'document',
        size: file.size || 1000000,
        url: previewUrl,
        thumbnailLink: file.thumbnailLink,
        uploadDate: new Date().toISOString().split('T')[0],
        lastModified: new Date(file.modifiedTime || Date.now()).toISOString().split('T')[0],
        status: 'uploading',
        progress: 5,
        processingSteps: [
          { label: 'Upload/Extract', status: 'active' },
          { label: 'OCR & Text', status: 'pending' },
          { label: 'Elements Detection', status: 'pending' },
          { label: 'BRD Matching', status: 'pending' },
          { label: 'Compliance Score', status: 'pending' }
        ]
      }
    })

    setAssets(prev => [...prev, ...driveAssets])
    setShowGoogleDrive(false)
    console.log('Imported from Google Drive:', driveAssets.length, 'files')

    // Process each Google Drive asset with AI analysis
    for (const asset of driveAssets) {
      await processGoogleDriveAsset(asset)
    }
  }, [])

  const processGoogleDriveAsset = async (asset: Asset) => {
    // Create a mock File object for Google Drive assets
    const mockFile = {
      name: asset.name,
      type: asset.type === 'image' ? 'image/jpeg' : asset.type === 'video' ? 'video/mp4' : 'application/pdf',
      size: asset.size
    }

    await processAssetWithSteps(asset, mockFile as File)
  }

  // Update asset data
  const handleUpdateAsset = useCallback((assetId: string, updates: any) => {
    setAssets(prev => prev.map(asset => 
      asset.id === assetId ? { ...asset, ...updates } : asset
    ))
  }, [])


    const getStatusStats = () => {
      const total = assets.length
      const complete = assets.filter(a => a.status === 'complete').length
      const analyzing = assets.filter(a => a.status === 'analyzing' || a.status === 'uploading').length
      const errors = assets.filter(a => a.status === 'error').length
      
      const failedChecks = assets.reduce((count, asset) => 
        count + (asset.analysis?.summary?.failed || 0), 0
      )
      const warningChecks = assets.reduce((count, asset) => 
        count + (asset.analysis?.summary?.warnings || 0), 0
      )
      const passedChecks = assets.reduce((count, asset) => 
        count + (asset.analysis?.summary?.passed || 0), 0
      )
      
      return { total, complete, analyzing, errors, failedChecks, warningChecks, passedChecks }
    }

  const stats = getStatusStats()

  // Sort assets based on selected sort order
  const sortedAssets = React.useMemo(() => {
    const sorted = [...assets]
    
    // Helper to get the most recent timestamp from an asset
    const getAssetTime = (asset: Asset) => {
      // Priority: lastSavedAt > lastModified > uploadDate
      if (asset.lastSavedAt) return new Date(asset.lastSavedAt).getTime()
      if (asset.lastModified) return new Date(asset.lastModified).getTime()
      if (asset.uploadDate) return new Date(asset.uploadDate).getTime()
      return 0
    }
    
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => getAssetTime(b) - getAssetTime(a)) // Newest first
      case 'oldest':
        return sorted.sort((a, b) => getAssetTime(a) - getAssetTime(b)) // Oldest first
      case 'name':
        return sorted.sort((a, b) => a.name.localeCompare(b.name))
      default:
        return sorted
    }
  }, [assets, sortBy])

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex justify-between items-center">
          <div>
            {props.campaignName && (
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {props.campaignName}
              </h1>
            )}
            {props.campaignDescription && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {props.campaignDescription}
              </p>
            )}
          </div>
                 <div className="flex items-center gap-4">
                   <Button 
                     variant="outline" 
                     onClick={() => window.location.href = '/rules'}
                     className="gap-2"
                   >
                     <Settings className="w-4 h-4" />
                     Manage Rules
                   </Button>
                   <Badge variant={brdRules.length > 0 ? "default" : "secondary"}>
                     {brdRules.length} Rules Active
                   </Badge>
                 </div>
        </div>
      </div>

      {/* Stats Bar */}
      <AssetStatsBar stats={stats} />

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'upload' | 'matrix')} className="flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upload">Asset Upload & Analysis</TabsTrigger>
          <TabsTrigger value="matrix">Creative Matrix ({assets.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="flex-1 flex">
          <div className="flex-1 overflow-hidden">
            {loadingAssets ? (
              <div className="h-full flex items-center justify-center p-6">
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Loading Campaign Assets
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Please wait while we fetch your assets...
                    </p>
                  </CardContent>
                </Card>
              </div>
            ) : assets.length === 0 ? (
              <div className="h-full flex items-center justify-center p-6">
                <Card 
                  className="border-2 border-dashed cursor-pointer hover:border-gray-400 transition-colors"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Upload className="w-16 h-16 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Processing Assets with AI
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center max-w-md">
                      Upload your creative assets to begin real-time BRD analysis and compliance checking.
                    </p>
                    <div className="flex gap-4">
                      <Button onClick={() => fileInputRef.current?.click()} data-testid="upload-button-select-files">
                        <Plus className="w-4 h-4 mr-2" />
                        Select Files
                      </Button>
                      <Button variant="outline" onClick={() => setShowGoogleDrive(true)} data-testid="import-button-google-drive">
                        <Cloud className="w-4 h-4 mr-2" />
                        Import from Drive
                      </Button>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      multiple
                      onChange={handleFileChange}
                      data-testid="upload-input-file"
                      className="hidden"
                      accept="image/*,video/*,.pdf,.doc,.docx"
                    />
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                {/* Table Header */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center mb-3">
                    <h2 className="text-lg font-semibold">Processing Assets with AI</h2>
                    <div className="flex gap-2">
                      <Button onClick={() => fileInputRef.current?.click()} size="sm" data-testid="upload-button-add-files">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Files
                      </Button>
                      <Button variant="outline" onClick={() => setShowGoogleDrive(true)} size="sm" data-testid="import-button-google-drive-secondary">
                        <Cloud className="w-4 h-4 mr-2" />
                        Import from Drive
                      </Button>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                      accept="image/*,video/*,.pdf,.doc,.docx"
                    />
                  </div>
                  
                  {/* View Controls */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {/* View Mode Toggle */}
                      <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-md">
                        <Button
                          variant={viewMode === 'cards' ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => setViewMode('cards')}
                          className="rounded-r-none"
                        >
                          <LayoutGrid className="w-4 h-4 mr-1" />
                          Cards
                        </Button>
                        <Button
                          variant={viewMode === 'table' ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => setViewMode('table')}
                          className="rounded-l-none"
                        >
                          <List className="w-4 h-4 mr-1" />
                          Table
                        </Button>
                      </div>
                      
                      {/* Sort Dropdown */}
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'name')}
                        className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="name">Name (A-Z)</option>
                      </select>
                    </div>
                    
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {assets.length} asset{assets.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>

                {/* Processing View */}
                <div className="flex-1 overflow-auto">
                  {viewMode === 'cards' ? (
                    /* Card View */
                    <div className="space-y-3 p-4">
                      {sortedAssets.map((asset) => (
                        <AssetCard 
                          key={asset.id}
                          asset={asset}
                          onClick={() => setSelectedAsset(asset)}
                        />
                      ))}
                    </div>
                  ) : (
                    /* Table View */
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Asset</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Size</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Save Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Compliance</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {sortedAssets.map((asset) => (
                            <AssetTableRow
                              key={asset.id}
                              asset={asset}
                              onClick={() => setSelectedAsset(asset)}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Side Panel Modal - Using AssetDetailPanel Component */}
          {selectedAsset && (
            <AssetDetailPanel
              asset={selectedAsset}
              onClose={() => setSelectedAsset(null)}
              onAssetUpdate={(updatedAsset) => {
                setAssets(prev => prev.map(a => a.id === updatedAsset.id ? updatedAsset : a))
                setSelectedAsset(updatedAsset)
                if (props.onAssetAdd) {
                  props.onAssetAdd(updatedAsset)
                }
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="matrix" className="flex-1 p-6 overflow-y-auto">
          {loadingAssets ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Loading Campaign Assets
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Please wait while we fetch your assets...
                </p>
              </CardContent>
            </Card>
          ) : assets.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Settings className="w-16 h-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No Assets for Creative Matrix
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Upload creative assets first to generate the Creative Matrix.
                </p>
                <Button onClick={() => setActiveTab('upload')}>
                  Upload Assets
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Export Buttons */}
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold">Creative Matrix</h2>
                  <p className="text-sm text-gray-500">Export your campaign in multiple formats</p>
                </div>
                <ExportMatrix 
                  assets={assets.map(asset => ({
                    id: asset.id,
                    fileName: asset.name,
                    format: asset.type,
                    fileSize: asset.size,
                    channel: asset.analysis?.fdaFilingNumber || 'Display',
                    indication: 'Pharma',
                    customerType: asset.analysis?.customerType || 'HCP',
                    complianceScore: asset.analysis?.complianceScore || 0,
                    thumbnailUrl: asset.url,
                    previewUrl: asset.url,
                    dimensions: { width: 300, height: 250 }
                  }))}
                  campaignName="BRD Campaign"
                />
              </div>

              {/* Creative Matrix Table */}
              <CreativeMatrix 
                assets={assets}
                brdRules={brdRules}
                onUpdateAsset={handleUpdateAsset}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Google Drive Browser */}
      {showGoogleDrive && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Import from Google Drive</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowGoogleDrive(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-hidden">
              <GoogleDriveBrowser
                onFileImport={handleGoogleDriveImport}
                multiSelect={true}
                showImportButton={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
